/**
 * Plain-words copy and price rules for the "Replace with another plan" modal.
 *
 * Kept free of React and of `utils.jsx` (which pulls in the Redux store) so the
 * rules can be tested on their own and the modal stays light in tests.
 */

import type {
  MembershipReplaceResult,
  ReplaceMembershipError,
} from '../../../hooks/membership/mutation/V2/useReplaceMembershipMutation';
import type { MembershipPriceGraphQL } from '../../../hooks/membership/query/V2/useMembershipsRelay';

// ============================================================================
// Formatting
// ============================================================================

export function formatCents(cents?: number | null): string {
  const value = (Number(cents) || 0) / 100;
  const [whole, fraction] = value.toFixed(2).split('.');
  return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction}`;
}

const UNIT_WORDS: Record<string, [string, string]> = {
  D: ['day', 'days'],
  W: ['week', 'weeks'],
  M: ['month', 'months'],
  Y: ['year', 'years'],
};

// ant stores intervals as hours ("PT720H"), the way the legacy admin saves
// them: 8760 h is a year and 720 h a month, as the legacy labels read them.
const HOUR_UNITS: Array<[number, string]> = [
  [8760, 'Y'],
  [720, 'M'],
  [168, 'W'],
  [24, 'D'],
];

function unitPhrase(count: number, unit: string): string {
  const [one, many] = UNIT_WORDS[unit];
  return count === 1 ? `every ${one}` : `every ${count} ${many}`;
}

/**
 * "P1M" or "PT720H" -> "every month", "P3M" -> "every 3 months",
 * none -> "no renewal".
 */
export function intervalPhrase(interval?: string | null): string {
  const match = interval?.match(/^P(\d+)([DWMY])$/);
  if (match) return unitPhrase(Number(match[1]), match[2]);
  const hours = Number(interval?.match(/^PT(\d+)H$/)?.[1]);
  if (!hours) return 'no renewal';
  const unit = HOUR_UNITS.find(([size]) => hours % size === 0);
  if (!unit) return hours === 1 ? 'every hour' : `every ${hours} hours`;
  return unitPhrase(hours / unit[0], unit[1]);
}

function shortDate(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toDateString();
}

export function isFreePrice(price: Pick<MembershipPriceGraphQL, 'price'>): boolean {
  return !price.price;
}

/** "Monthly · $12.00 every month, renews automatically". */
export function priceLabel(price: MembershipPriceGraphQL): string {
  const amount = isFreePrice(price) ? 'Free' : formatCents(price.price);
  const parts = [amount];
  if (price.termStartsOn || price.termEndsOn) {
    parts.push(`for ${shortDate(price.termStartsOn)} – ${shortDate(price.termEndsOn)}`);
  } else {
    parts.push(intervalPhrase(price.renewalInterval));
  }
  if (price.autoRenews) parts.push('renews automatically');
  const description = parts.join(', ');
  return price.name ? `${price.name} · ${description}` : description;
}

// ============================================================================
// Price compatibility (mirrors ant's interval_mismatch and price_increase rules)
// ============================================================================

/**
 * Why `to` cannot take riders from `from`, or null when it can. ant refuses a
 * mapping that changes the renewal interval or auto-renew (billing only renews
 * auto-renewing prices), and any new price with an archive date, even a
 * future one. ant also refuses a new price that costs more than the old one. Fixed-term coverage depends on each
 * rider's term, so only ant can check it; its refusal shows on the review step.
 */
export function priceIncompatibility(
  from: MembershipPriceGraphQL,
  to: MembershipPriceGraphQL
): string | null {
  if (to.archivedOn) return 'This price is archived or scheduled to be archived.';
  if ((from.renewalInterval ?? null) !== (to.renewalInterval ?? null)) {
    return `Renews ${intervalPhrase(
      to.renewalInterval
    )}; the old price renews ${intervalPhrase(from.renewalInterval)}.`;
  }
  if (Boolean(from.autoRenews) !== Boolean(to.autoRenews)) {
    return from.autoRenews
      ? 'Does not renew automatically; the old price does.'
      : 'Renews automatically; the old price does not.';
  }
  if ((to.price ?? 0) > (from.price ?? 0)) {
    return `Costs ${formatCents(to.price)}, more than the old ${formatCents(
      from.price
    )}. A replace cannot raise a rider's price.`;
  }
  return null;
}

// ============================================================================
// Refusals
// ============================================================================

function idList(vars: Record<string, unknown>, key: string): string[] {
  const value = vars[key];
  return Array.isArray(value) ? value.map(String) : [];
}

export function refusalText(
  error: ReplaceMembershipError,
  names: { oldName: string; newName: string }
): string {
  const { oldName, newName } = names;
  switch (error.code) {
    case 'forbidden':
      return 'Only Movatic admins can replace a plan.';
    case 'membership_not_found':
      return `"${oldName}" was not found. Reload the page and try again.`;
    case 'replacement_not_found':
      return `"${newName}" was not found. Pick another plan.`;
    case 'same_membership':
      return 'A plan cannot replace itself. Pick another plan.';
    case 'different_system':
      return 'Both plans must belong to the same system.';
    case 'replacement_archived':
      return `"${newName}" is archived or has an archive date. Pick a plan with no archive date.`;
    case 'membership_archived':
      return `"${oldName}" is already archived.`;
    case 'go_membership':
      return 'The GO plan has special billing and cannot be replaced here.';
    case 'unmapped_price': {
      const count = idList(error.vars, 'price_ids').length;
      return `Riders on "${oldName}" use ${
        count === 1 ? 'a price' : `${count || 'some'} prices`
      } with no matching price on "${newName}". Map every price below.`;
    }
    case 'interval_mismatch':
      return 'A new price renews differently from its old price (interval or auto-renew). Map each old price to one that renews the same way.';
    case 'price_increase':
      return 'A new price costs more than its old price. Map each old price to one that costs the same or less.';
    case 'fixed_term_not_covered': {
      const count = idList(error.vars, 'user_membership_ids').length;
      return `The new price's fixed term does not cover the current term of ${
        count === 1 ? '1 rider' : `${count || 'some'} riders`
      }. Pick a price whose term covers theirs, or extend its term on "${newName}".`;
    }
    case 'invalid_price_map':
      return error.message;
    default:
      return error.message || 'The plan could not be replaced.';
  }
}

// ============================================================================
// Review
// ============================================================================

function riders(count: number): string {
  return count === 1 ? '1 rider' : `${count} riders`;
}

export interface ReviewLine {
  key: string;
  text: string;
  /** "warning" lines need the operator's attention after the move. */
  tone: 'info' | 'warning';
}

/** Distinct "old -> new" charge changes among the paid samples. */
function chargeChanges(result: MembershipReplaceResult): string[] {
  const seen = new Set<string>();
  result.paidSameInterval.samples.forEach((sample) => {
    if (sample.oldAmount == null || sample.newAmount == null) return;
    seen.add(
      sample.oldAmount === sample.newAmount
        ? `${formatCents(sample.oldAmount)} (unchanged)`
        : `${formatCents(sample.oldAmount)} → ${formatCents(sample.newAmount)}`
    );
  });
  return Array.from(seen);
}

export function reviewLines(
  result: MembershipReplaceResult,
  names: { oldName: string; newName: string }
): ReviewLine[] {
  const { oldName, newName } = names;
  const lines: ReviewLine[] = [];
  const will = result.dryRun;
  // Verb agreement: n === 1 takes the singular form.
  const v = (n: number, one: string, many: string) => (n === 1 ? one : many);

  const moved = result.movedCount;
  lines.push({
    key: 'moved',
    text: `${riders(moved)} ${
      will ? v(moved, 'moves', 'move') : 'moved'
    } to "${newName}".`,
    tone: 'info',
  });

  const paid = result.paidSameInterval.count;
  if (paid > 0) {
    const changes = chargeChanges(result);
    const next = changes.length ? `; next charge ${changes.join(', ')}` : '';
    lines.push({
      key: 'paid',
      text: `${riders(paid)} on a paid price ${v(
        paid,
        'keeps',
        'keep'
      )} their renewal date${next}. Nobody is prorated.`,
      tone: 'info',
    });
  }
  if (result.free.count > 0) {
    lines.push({
      key: 'free',
      text: `${riders(result.free.count)} on a free price.`,
      tone: 'info',
    });
  }
  const pending = result.pending.count;
  if (pending > 0) {
    lines.push({
      key: 'pending',
      text: `${riders(pending)} waiting for approval or email verification ${v(
        pending,
        'stays',
        'stay'
      )} pending on "${newName}".`,
      tone: 'info',
    });
  }
  const canceled = result.canceledButPaid.count;
  if (canceled > 0) {
    lines.push({
      key: 'canceled',
      text: `${riders(canceled)} who canceled but paid through the current term ${v(
        canceled,
        'runs',
        'run'
      )} to the end of it on "${newName}".`,
      tone: 'info',
    });
  }
  const usage = result.withUsage.count;
  if (usage > 0) {
    lines.push({
      key: 'usage',
      text: `${riders(usage)} ${v(
        usage,
        'keeps',
        'keep'
      )} the minutes and unlocks already used; they count against "${newName}".`,
      tone: 'info',
    });
  }
  const email = result.emailDomainExceptions.count;
  if (email > 0) {
    lines.push({
      key: 'email',
      text: `${riders(email)} ${v(
        email,
        'has',
        'have'
      )} an email "${newName}" would not accept; they move anyway.`,
      tone: 'warning',
    });
  }
  const collisions = result.collisions.count;
  if (collisions > 0) {
    const stay = will ? v(collisions, 'stays', 'stay') : 'stayed';
    lines.push({
      key: 'collisions',
      text: `${riders(
        collisions
      )} already on "${newName}" ${stay} where they are; their "${oldName}" membership is marked as replaced.`,
      tone: 'warning',
    });
  }
  const qr = result.qrCodeCount;
  if (qr > 0) {
    lines.push({
      key: 'qr',
      text: `${v(
        qr,
        '1 QR code still points',
        `${qr} QR codes still point`
      )} at "${oldName}". Repoint or retire ${v(qr, 'it', 'them')} after the move.`,
      tone: 'warning',
    });
  }

  const prices = result.pricesToArchive.length;
  const perks = result.perksToArchive.length;
  const archived = `${v(prices, '1 price', `${prices} prices`)} and ${v(
    perks,
    '1 perk',
    `${perks} perks`
  )}`;
  lines.push({
    key: 'archive',
    text: will
      ? `"${oldName}" is archived with its ${archived}, and shows "Replaced by ${newName}".`
      : `"${oldName}" was archived with its ${archived}.`,
    tone: 'info',
  });

  return lines;
}

/** Toast after the real run. */
export function successMessage(
  result: MembershipReplaceResult,
  names: { oldName: string; newName: string }
): string {
  if (result.status === 'already_replaced') {
    return `"${names.oldName}" was already replaced by "${names.newName}". Nothing changed.`;
  }
  const collisions =
    result.collisions.count > 0
      ? ` ${riders(result.collisions.count)} ${
          result.collisions.count === 1 ? 'was' : 'were'
        } already on it.`
      : '';
  return `Moved ${riders(result.movedCount)} to "${names.newName}" and archived "${
    names.oldName
  }".${collisions}`;
}
